import { Currency, CurrencyAmount, TradeType } from '@kodiak-finance/sdk-core'
import { isSupportedChain } from 'config/chains'
import { useMemo } from 'react'
import { Trade, TradeState } from 'state/routing/trades'
import { useRoutingAPITrade } from 'state/routing/useRoutingAPITrade'

import { useConnection } from './useConnection'
import useDebounce from './useDebounce'
import useIsWindowVisible from './useIsWindowVisible'

/**
 * Returns the best trade for a desired swap.
 * @param tradeType whether the swap is an exact in/out
 * @param providers array of provider names to include in aggregation
 * @param amountSpecified the exact amount to swap in/out
 * @param otherCurrency the desired output/payment currency
 * @param pauseFetch pause fetching if true
 * @param resolvedRecipient recipient address for the trade
 */
export function useBestTrade(
  tradeType: TradeType,
  providers: string[],
  amountSpecified?: CurrencyAmount<Currency>,
  otherCurrency?: Currency,
  pauseFetch?: boolean,
  resolvedRecipient?: string | null
): {
  state: TradeState
  trades: Trade[]
  bestTrade: Trade | undefined
  isQuoteSyncing: boolean
  refetch: () => void
  isRefetching: boolean
} {
  const { chainId } = useConnection()
  const isWindowVisible = useIsWindowVisible()

  const debouncedParams = useDebounce(
    useMemo(
      () => ({
        tradeType,
        amountSpecified,
        otherCurrency,
      }),
      [tradeType, amountSpecified, otherCurrency]
    ),
    200
  )

  const routingAPITrade = useRoutingAPITrade(
    debouncedParams.tradeType,
    providers,
    isSupportedChain(chainId) && isWindowVisible ? debouncedParams.amountSpecified : undefined,
    debouncedParams.otherCurrency,
    pauseFetch,
    resolvedRecipient
  )

  return useMemo(
    () => ({
      state: routingAPITrade.state,
      trades: routingAPITrade.trades,
      bestTrade: routingAPITrade.bestTrade,
      isQuoteSyncing: routingAPITrade.state === TradeState.SYNCING,
      refetch: routingAPITrade.refetch,
      isRefetching: routingAPITrade.isRefetching,
    }),
    [routingAPITrade]
  )
}
